<script setup lang="ts">
import { onMounted, ref } from "vue";
import type { DashboardSummary } from "@wjbk/workbench-contracts";
import { api } from "../api";

const summary = ref<DashboardSummary>({ productCount: 0, pendingApprovalCount: 0, enabledStaffCount: 0, integrationFailureCount: 0 });
const error = ref("");
const loading = ref(true);

onMounted(async () => {
  try {
    summary.value = await api.dashboard();
  } catch (e) {
    error.value = (e as Error).message;
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <div v-if="error" class="error">{{ error }}。页面不会使用模拟数字代替正式业务数据。</div>

  <section class="action-metrics" aria-label="今日重点待办">
    <RouterLink to="/approvals" class="action-card action-card-blue">
      <div><span class="action-icon">审</span><small>待我审批</small></div>
      <strong v-if="!loading">{{ summary.pendingApprovalCount }}</strong><strong v-else>—</strong>
      <p>查看需要我处理的申请</p>
    </RouterLink>
    <RouterLink to="/lifecycle" class="action-card action-card-violet">
      <div><span class="action-icon">产</span><small>待我放行的产品</small></div>
      <strong>—</strong>
      <p><em>待接入</em> 生命周期责任人数据</p>
    </RouterLink>
    <RouterLink to="/budgets" class="action-card action-card-amber">
      <div><span class="action-icon">预</span><small>超预算待处理</small></div>
      <strong>—</strong>
      <p><em>待接入</em> 预算与升级审批</p>
    </RouterLink>
    <RouterLink to="/alerts" class="action-card action-card-red">
      <div><span class="action-icon">异</span><small>同步 / 成本异常</small></div>
      <strong v-if="!loading">{{ summary.integrationFailureCount }}</strong><strong v-else>—</strong>
      <p>查看失败同步和待确认成本</p>
    </RouterLink>
  </section>

  <section class="dashboard-columns">
    <article class="panel dashboard-panel">
      <div class="panel-header">
        <div><h2>我的待办</h2><p>按优先级和截止时间集中处理</p></div>
        <RouterLink to="/tasks" class="text-link">查看全部 →</RouterLink>
      </div>
      <div class="list-head"><span>事项</span><span>关联对象</span><span>截止时间</span><span>状态</span></div>
      <div class="honest-empty compact-empty">
        <span class="empty-symbol">✓</span>
        <div><strong>暂无待办记录</strong><p>审批、阶段放行和成本确认接入后会自动汇总在这里。</p></div>
      </div>
    </article>

    <article class="panel dashboard-panel">
      <div class="panel-header">
        <div><h2>我负责的产品</h2><p>关注当前阶段、下一节点和阻塞原因</p></div>
        <RouterLink to="/products" class="text-link">产品台账 →</RouterLink>
      </div>
      <div v-if="summary.productCount === 0" class="honest-empty">
        <span class="empty-symbol box-symbol">□</span>
        <div><strong>尚未导入产品主数据</strong><p>后续从工作台立项，或映射商城 SPU / SKU 与 ERPNext Item。</p></div>
      </div>
      <div v-else class="product-count">
        <strong>{{ summary.productCount }}</strong><span>个产品已进入统一产品台账</span>
        <RouterLink to="/products">打开台账</RouterLink>
      </div>
    </article>
  </section>

  <section class="panel overview-panel">
    <div class="panel-header">
      <div><h2>经营概况</h2><p>不同岗位只显示其权限范围内的指标；未接入的数据明确标注。</p></div>
      <span class="data-note">数据口径：待建立</span>
    </div>
    <div class="role-overview">
      <div><span class="overview-mark blue">营</span><small>运营</small><strong>上市计划</strong><em>待接入</em><p>销售、退款与营销排期</p></div>
      <div><span class="overview-mark violet">产</span><small>生产</small><strong>试产 / 批次</strong><em>待接入</em><p>阶段放行和批次成本异常</p></div>
      <div><span class="overview-mark amber">财</span><small>财务</small><strong>费用 / 待付款</strong><em>待接入</em><p>支出、报销和供应商付款</p></div>
      <div><span class="overview-mark green">管</span><small>管理者</small><strong>跨部门汇总</strong><em>待接入</em><p>产品贡献利润与执行进度</p></div>
    </div>
  </section>

  <section class="dashboard-footer-grid">
    <article class="panel access-panel">
      <div class="panel-header"><div><h2>业务应用</h2><p>入口与实际接入程度分开标识</p></div><RouterLink to="/integrations" class="text-link">管理应用 →</RouterLink></div>
      <div class="mini-apps">
        <div><b>商</b><span><strong>小程序商城</strong><small>适配器待联调</small></span></div>
        <div><b>ERP</b><span><strong>ERPNext</strong><small>成本接口待确认</small></span></div>
        <div><b>客</b><span><strong>客服系统</strong><small>统一入口已部署</small></span></div>
        <div><b>禾</b><span><strong>小禾堂客服</strong><small>入口已配置</small></span></div>
      </div>
    </article>
    <article class="panel readiness-panel">
      <div class="panel-header"><div><h2>工作台准备度</h2><p>当前真实建设状态</p></div><span class="status-dot-label"><i></i>基础框架运行中</span></div>
      <ul>
        <li><span>组织与权限数据模型</span><b>已建立</b></li>
        <li><span>统一产品与费用模型</span><b>已建立</b></li>
        <li><span>外部系统真实数据</span><em>待联调</em></li>
      </ul>
    </article>
  </section>
</template>
